import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { events } from '../../../mocks/events';
import { supabase } from '../../../lib/supabase';

export default function Hero() {
  const navigate = useNavigate();
  const [upcomingEvents, setUpcomingEvents] = useState<typeof events>([]);
  const [liveStreams, setLiveStreams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Filter for upcoming events from January 2026 onwards
    const now = new Date('2026-01-01');
    const upcoming = events
      .filter(event => {
        const eventDate = new Date(event.date);
        return eventDate >= now && 
               event.category !== 'National Holiday' &&
               (event.organizer === 'General Conference' || 
                event.organizer === 'West-Central Africa Division' ||
                event.organizer.includes('Union'));
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 3);
    
    setUpcomingEvents(upcoming);
  }, []);

  // Fetch live streams from Supabase
  useEffect(() => {
    const fetchLiveStreams = async () => {
      try {
        const { data, error } = await supabase
          .from('live_streams')
          .select('*')
          .eq('is_live', true)
          .order('started_at', { ascending: false })
          .limit(3);

        if (error) {
          console.warn('Could not fetch live streams:', error.message);
          setLiveStreams([]);
          setLoading(false);
          return;
        }

        setLiveStreams(data || []);
      } catch (err: any) {
        console.warn('Live streams unavailable:', err.message);
        setLiveStreams([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLiveStreams();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchLiveStreams();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleLearnMore = (eventId: number) => {
    navigate(`/events?eventId=${eventId}`);
  };

  // Generate random sparkle positions and colors
  const sparkles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    top: Math.random() * 100,
    left: Math.random() * 100,
    size: Math.random() * 20 + 15, // 15-35px
    delay: Math.random() * 5,
    duration: Math.random() * 3 + 4, // 4-7s
    color: ['emerald', 'teal', 'cyan', 'white', 'slate'][Math.floor(Math.random() * 5)],
    opacity: Math.random() * 0.3 + 0.1, // 0.1-0.4
  }));

  return (
    <div className="relative bg-slate-900 text-white overflow-hidden">
      {/* Background Video */}
      <div className="absolute inset-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover object-center"
        >
          <source src="https://public.readdy.ai/ai/video_res/58ea36f7-f36b-4f8a-92cc-1386983aaa11.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-800/90 to-teal-900/85"></div>
      
      {/* Floating Logo Sparkles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {sparkles.map((sparkle) => (
          <div
            key={sparkle.id}
            className="absolute animate-float-sparkle"
            style={{
              top: `${sparkle.top}%`,
              left: `${sparkle.left}%`,
              width: `${sparkle.size}px`,
              height: `${sparkle.size}px`,
              animationDelay: `${sparkle.delay}s`,
              animationDuration: `${sparkle.duration}s`,
              opacity: sparkle.opacity,
            }}
          >
            <img
              src="https://static.readdy.ai/image/b26ccc3ae7fa45b3398224fbc482abd6/074d3102ffad2a455591f49f105c88ed.png"
              alt=""
              className="w-full h-full object-contain"
              style={{
                filter: sparkle.color === 'emerald' ? 'brightness(0) saturate(100%) invert(64%) sepia(98%) saturate(459%) hue-rotate(101deg) brightness(91%) contrast(86%)' :
                        sparkle.color === 'teal' ? 'brightness(0) saturate(100%) invert(60%) sepia(51%) saturate(1345%) hue-rotate(133deg) brightness(91%) contrast(86%)' :
                        sparkle.color === 'cyan' ? 'brightness(0) saturate(100%) invert(79%) sepia(26%) saturate(1234%) hue-rotate(142deg) brightness(94%) contrast(86%)' :
                        sparkle.color === 'white' ? 'brightness(0) saturate(100%) invert(100%)' :
                        'brightness(0) saturate(100%) invert(71%) sepia(8%) saturate(434%) hue-rotate(169deg) brightness(92%) contrast(87%)'
              }}
            />
          </div>
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full mb-6 sm:mb-8">
            <span className="text-xs sm:text-sm font-semibold text-white whitespace-nowrap">National Digital Worship Infrastructure</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 sm:mb-8 leading-tight" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
            Empowering Every<br />Seventh-day Adventist<br />Church in Nigeria
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-10 sm:mb-14 max-w-3xl mx-auto leading-relaxed">
            Connecting believers through innovative digital solutions
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-12 sm:mb-16">
            <Link
              to="/directory"
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-emerald-600 text-white rounded-lg font-bold text-base sm:text-lg hover:bg-emerald-700 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer text-center"
            >
              Explore Churches
            </Link>
            <Link
              to="/tools"
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white text-slate-800 rounded-lg font-bold text-base sm:text-lg hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer text-center"
            >
              Worship Tools
            </Link>
            <Link
              to="/live"
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-transparent border-2 border-white text-white rounded-lg font-bold text-base sm:text-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer text-center"
            >
              Live Now
            </Link>
          </div>
        </div>
        
        {/* Live Streams Grid */}
        {liveStreams.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {liveStreams.map((stream) => (
              <a
                key={stream.id}
                href={stream.youtube_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl border border-gray-200"
              >
                <div className="relative">
                  <div className="w-full h-40 sm:h-48">
                    <img
                      src={stream.thumbnail_url || `https://public.readdy.ai/ai/img_res/dc763dc0ad0137d03737de1262b2a59b.jpg${stream.church_name.toLowerCase().replace(/\s+/g, '-')}-live&orientation=landscape`}
                      alt={stream.church_name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  
                  {/* Live Badge */}
                  <div className="absolute top-3 left-3 px-3 py-1 bg-rose-500 text-white text-xs font-bold rounded-full flex items-center space-x-1 animate-pulse">
                    <span className="w-2 h-2 bg-white rounded-full"></span>
                    <span>LIVE</span>
                  </div>

                  {/* Viewer Count */}
                  {stream.viewer_count !== null && stream.viewer_count !== undefined && (
                    <div className="absolute top-3 right-3 px-2 py-1 bg-black/70 text-white text-xs font-semibold rounded">
                      {stream.viewer_count.toLocaleString()}
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <h3 className="font-bold text-gray-900 mb-2 text-sm group-hover:text-teal-600 transition-colors">
                    {stream.church_name}
                  </h3>
                  
                  <div className="space-y-1 text-xs text-gray-600">
                    <p className="flex items-center">
                      <i className="ri-map-pin-line mr-1 text-teal-600"></i>
                      {stream.state}, {stream.region}
                    </p>
                    <p className="flex items-center">
                      <i className="ri-building-line mr-1 text-teal-600"></i>
                      {stream.conference}
                    </p>
                    {stream.stream_title && (
                      <p className="flex items-center">
                        <i className="ri-live-line mr-1 text-rose-500"></i>
                        <span className="truncate">{stream.stream_title}</span>
                      </p>
                    )}
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}

        <div className="flex items-center justify-center space-x-2 text-white/70 animate-bounce mt-8">
          <span className="text-xs sm:text-sm whitespace-nowrap">Scroll to explore</span>
          <i className="ri-arrow-down-line"></i>
        </div>
      </div>
    </div>
  );
}
